/* eslint-disable react/no-array-index-key */

import moment from 'moment';
import { groupBy, isEqual, uniq } from 'lodash';
import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import { withRouter } from 'react-router-dom';

import { AiOutlineMenuFold } from 'react-icons/ai';
import { FiCopy, FiCheckCircle, FiXCircle, FiCloud } from 'react-icons/fi';
import { BsFillExclamationTriangleFill } from 'react-icons/bs';

import { Collection } from 'core/model';
import Page from 'app/components/page/Page';
import { cloudProviderData } from 'app/util/utils';
import { getQueriesForHash } from 'app/stores/query/urlHash';
import { getConsoleUrl } from 'app/views/hybrid/utils/aws';
import { DEFAULT_DATETIME_FORMAT } from 'core/util/dateUtils';
import LoadingSkeleton from 'app/views/metrics/LoadingSkeleton';
import LargeLabeledValue from 'app/components/LargeLabeledValue';
import { getCustomProperties } from '@kentik/ui-shared/util/map';
import { ENTITY_TYPES } from '@kentik/ui-shared/hybrid/constants';
import CloudIcon from 'app/views/hybrid/maps/components/CloudIcon';
import CloudItem from 'app/views/hybrid/maps/components/CloudItem';
import { getAwsAccountId } from '@kentik/ui-shared/util/aws/utils';
import CopyToClipboardButton from 'app/components/CopyToClipboardButton';
import CloudLinkWithIcon from 'app/views/cloudConnectivity/CloudLinkWithIcon';
import { ReactComponent as KentikIcon } from 'app/assets/logos/kentik-icon.svg';
import withPopover from 'app/views/hybrid/maps/components/popovers/withPopover';
import { makeConfig } from 'app/views/hybrid/maps/components/popovers/configHelper';
import HybridTopoSidebar from 'app/views/hybrid/maps/components/HybridTopoSidebar';
import withSidebarDetails from 'app/views/hybrid/maps/components/popovers/withSidebarDetails';
import {
  Box,
  Flex,
  Card,
  Spinner,
  Icon,
  Text,
  Suspense,
  Tooltip,
  Popover,
  Heading,
  Button,
  LinkButton,
  Callout,
  Dismissible
} from 'core/components';
import { Table } from 'core/components/table';

import Markdown from 'app/views/core/journeys/Markdown';
import CalloutKentikAi from 'core/components/CalloutKentikAI';
import { convertToReadableId, convertToReadableType } from './utils';

const ICON_SIZE = 16;
const { VPC, CORE_NETWORK_SEGMENT, TRANSIT_GATEWAY, FIREWALL } = ENTITY_TYPES.get('aws');
const { VNET } = ENTITY_TYPES.get('azure');

const GLOBAL_REGION = 'Global';

const SummaryWrapper = styled.div`
  h5 {
    margin: 24px 0 8px 0;
  }

  h5:first-child {
    margin-top: 0;
  }
`;

const PathScrollContainer = styled(Box)`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  margin-bottom: 10px;
`;

@withPopover
@withTheme
@withRouter
@withSidebarDetails
@inject('$clouds', '$hybridMap', '$auth')
@observer
export default class ConnectivityReport extends Component {
  static defaultProps = {
    loadingText: 'Your Cloud Pathfinder Report is on its way, thanks for your patience...'
  };

  // TODO - rename all "Connectivity Checker" components to reflect new ***marketing-approved*** name "Cloud Pathfinder"
  state = {
    drawerIsOpen: false,
    loadingTopology: false,
    paths: [],
    loading: true,
    returnPaths: [],
    error: undefined,
    report: undefined,
    srcEntity: undefined,
    dstEntity: undefined
  };

  get cloud() {
    const { report } = this.state;
    return report?.get('cloud') ?? 'aws';
  }

  async fetchAwsTopology(awsAccount) {
    const { $hybridMap } = this.props;

    await $hybridMap.awsCloudMapCollection.fetch({ data: { accountIds: [awsAccount] } });

    return $hybridMap.awsCloudMapCollection.initialTopology;
  }

  async convertQueryParamsToReport() {
    const { match, $clouds, reportObject } = this.props;
    const { reportIdOrHash } = match.params;

    // Accept report object as prop, for embedding component
    if (reportObject) {
      return $clouds.connectivityReportCollection.forge(reportObject);
    }

    // if reportIdOrHash is a number, it's a report id
    if (+reportIdOrHash) {
      // try to get it from collection first, it will be loaded if page was opened from main report page
      const collectionModel = $clouds.connectivityReportCollection.get(+reportIdOrHash);
      if (collectionModel) {
        return collectionModel;
      }

      // if direct link, initialize the model from database
      const reportModel = $clouds.connectivityReportCollection.forge({ id: +reportIdOrHash });
      return reportModel.fetch().then(() => reportModel);
    }

    // url is opened by hash, decode the hash and initialize report model
    return getQueriesForHash(reportIdOrHash).then((queries) => {
      if (!Array.isArray(queries)) {
        return null;
      }

      if (queries.length === 0) {
        return null;
      }

      return $clouds.connectivityReportCollection.forge({ ...queries[0] });
    });
  }

  componentDidMount() {
    let originalReportResults;
    this.convertQueryParamsToReport()
      .then((reportModel) => {
        originalReportResults = reportModel.get('results');
        return this.fetchReportDetails(reportModel);
      })
      .then((report) => {
        // save report to db to sync status
        if (!report.isNew && !isEqual(originalReportResults, report.get('results'))) {
          report.save();
        }
      })
      .catch((error) => {
        this.setState({ error, loading: false });
      });
  }

  componentDidUpdate(prevProps) {
    const { match } = this.props;
    const { reportIdOrHash } = match.params;

    if (reportIdOrHash !== prevProps.match.params.reportIdOrHash) {
      this.componentDidMount();
    }
  }

  async fetchReportDetails(report, forceRebuild = false) {
    const { $clouds } = this.props;

    if (!report) {
      this.setState({ loading: false, error: 'Unable to initialize report.' });
      return report;
    }

    return $clouds.buildConnectivityReport(report, forceRebuild).then((connectivityReport) => {
      const { paths, returnPaths, error, results, srcEntity, dstEntity } = connectivityReport;
      report.set('results', results);
      this.setState({ report, paths, returnPaths, error, srcEntity, dstEntity, loading: false });

      return report;
    });
  }

  handleShowDetails = async (segment) => {
    const { setSidebarDetails } = this.props;
    const { entity, type, value } = segment;

    const accountId = getAwsAccountId(entity);
    this.setState({ drawerIsOpen: true, loadingTopology: true });
    const topology = await this.fetchAwsTopology(accountId);

    const config = makeConfig({
      cloudProvider: this.cloud,
      topology,
      type,
      value,
      nodeData: entity,
      details: {
        panelTitle: 'Entity Details Summary',
        node: entity,
        keys: Object.keys(entity)
      }
    });

    setSidebarDetails(config);
    this.setState({ loadingTopology: false });
  };

  renderCloudItem = (segment) => {
    const { openPopover } = this.props;

    const hasErrors = segment.errors?.length > 0;
    const { type, value, region, entity } = segment;

    const title = convertToReadableId(
      segment.entity?.Name ?? segment.entity?.id ?? segment.entity?.CidrBlock ?? segment.value,
      this.cloud
    );

    let subtitle = '';
    const isAttachment = value?.includes('attachment-') || value?.includes('tgw-attach-');
    if (isAttachment) {
      subtitle = `${entity?.AttachmentType?.toUpperCase() || entity?.ResourceType.toUpperCase()} Attachment`;
    } else {
      subtitle = this.getSegmentIpAddress(segment) ?? [];
    }

    if (type === CORE_NETWORK_SEGMENT) {
      subtitle = 'Core Network Segment';
    }

    if (type === TRANSIT_GATEWAY) {
      subtitle = entity?.Description;
    }

    const popoverMenuItems = [
      {
        key: `${this.cloud} Map`,
        text: `${this.cloud === 'aws' ? 'AWS' : 'Azure'} Map`,
        icont: <KentikIcon style={{ width: 15, height: 15 }} />,
        action: () => window.open(`/v4/kentik-map/cloud/${this.cloud}`, '_blank')
      }
    ];

    if (this.cloud === 'aws') {
      popoverMenuItems.push({
        key: `show-details-${type}-${value}`,
        text: 'Show Details',
        icon: 'panel-stats',
        action: () => this.handleShowDetails(segment)
      });
      popoverMenuItems.push({
        key: 'AWS Console',
        text: 'Show in AWS Console',
        icon: cloudProviderData(this.cloud, 'logo'),
        action: () => {
          const url = getConsoleUrl({
            type,
            value,
            nodeData: {
              ...entity,
              RegionName: region
            }
          });
          window.open(url, '_blank');
        }
      });

      (segment?.errors ?? []).forEach((error) => {
        (error?.nodes ?? []).forEach((nodeId) => {
          popoverMenuItems.push({
            key: nodeId,
            icon: 'offline',
            bg: 'warningBackground',
            text: `Resolve ${nodeId} in AWS Console`,
            action: () => {
              const url = getConsoleUrl({
                type: '',
                value: nodeId,
                nodeData: {
                  RegionName: region
                }
              });
              window.open(url, '_blank');
            }
          });
        });
      });
    }

    return (
      <Tooltip
        intent="danger"
        content={
          hasErrors ? (
            <Box>
              {segment.errors?.map((error) => (
                <Text key={error.message} as="div">
                  {error.message}
                </Text>
              ))}
            </Box>
          ) : null
        }
      >
        <Box>
          <svg width={200} height={35}>
            <CloudItem
              width={200}
              height={35}
              stroke={hasErrors ? '#db3737' : '#3d7fb2'}
              title={title}
              subtitle={subtitle}
              icon={
                <CloudIcon
                  cloudProvider={this.cloud}
                  entity={segment.type}
                  width={ICON_SIZE + 5}
                  height={ICON_SIZE + 5}
                />
              }
              onClick={() =>
                openPopover({
                  isEmbedded: true,
                  shortcutMenu: {
                    hideShowConnectionsMenuItem: true,
                    customItems: popoverMenuItems
                  }
                })
              }
            />
          </svg>
        </Box>
      </Tooltip>
    );
  };

  renderRightArrow = (size = 32) => (
    <Flex flexDirection="column" justifyContent="center" alignItems="center">
      <Icon icon="arrow-right" color="muted" iconSize={size} />
    </Flex>
  );

  renderTextCopyButton = (text) => (
    <CopyToClipboardButton text={text} copyConfirmationText={null} intent="none" ml="4px" minimal small>
      <Button icon={FiCopy} />
    </CopyToClipboardButton>
  );

  getMatchingRouteDetails = (pathSegment) => {
    const matchingRoute = pathSegment?.matchingRoute;

    if (!matchingRoute) {
      return null;
    }

    const destinations = (matchingRoute.Destinations ?? [])
      .map((destination) => destination?.ResourceId ?? destination?.CoreNetworkAttachmentId)
      .filter(Boolean)
      .join(', ');

    return [
      { label: 'Destination', value: matchingRoute.DestinationCidrBlock ?? matchingRoute.matchingDestination?.address },
      { label: 'Origin', value: matchingRoute.Origin },
      { label: 'State', value: matchingRoute.State },
      { label: 'Type', value: matchingRoute.Type },
      { label: 'Gateway', value: matchingRoute.GatewayId },
      { label: 'Core Network ARN', value: matchingRoute.CoreNetworkArn },
      { label: 'Destinations', value: destinations || null },
      { label: 'Priority', value: matchingRoute.priority }
    ].filter(({ value }) => value !== undefined && value !== null && value !== '');
  };

  getMatchingRouteColumns = () => [
    {
      name: 'label',
      label: 'Field',
      width: 130,
      minWidth: 130,
      renderer: ({ value }) => (
        <Text as="span" fontWeight="bold">
          {value}
        </Text>
      )
    },
    {
      name: 'value',
      label: 'Value',
      renderer: ({ value }) => String(value)
    }
  ];

  renderMatchingRoutePopover = (pathSegment) => {
    const routeDetails = this.getMatchingRouteDetails(pathSegment);
    if (!routeDetails) {
      return null;
    }

    const routeDetailsCollection = new Collection(routeDetails);

    return (
      <Popover
        position="top"
        content={
          <Box p={1} style={{ maxWidth: 360 }}>
            <Text as="div" fontWeight="bold" mb={1}>
              Matching Route
            </Text>
            <Table
              collection={routeDetailsCollection}
              columns={this.getMatchingRouteColumns()}
              selectOnRowClick={false}
              hideHeader
            />
          </Box>
        }
      >
        <Button icon={<CloudIcon cloudProvider="aws" entity="route" width={20} height={20} />} intent="none" />
      </Popover>
    );
  };

  getPathNetworkType = (pathSegment) => {
    const { type, entity } = pathSegment;

    if (this.cloud === 'aws') {
      if (type === VPC || entity?.VpcId || entity?.Vpc?.id) {
        return VPC;
      }

      if (type === CORE_NETWORK_SEGMENT || entity?.CoreNetworkId) {
        return CORE_NETWORK_SEGMENT;
      }

      if (entity?.TransitGatewayId) {
        return TRANSIT_GATEWAY;
      }
    }

    return null;
  };

  getPathNetworkName = (pathSegment) => {
    const { entity, type } = pathSegment;

    if (this.cloud === 'aws') {
      if (type === VPC) {
        return entity?.Name ?? null;
      }

      if (entity?.Vpc?.Name) {
        return entity?.Vpc?.Name;
      }

      if (entity?.CoreNetworkId) {
        return pathSegment?.coreNetworkName ?? entity?.CoreNetworkId ?? '';
      }

      if (entity?.TransitGatewayId) {
        return `${pathSegment?.transitGatewayName ?? entity?.Name ?? entity?.TransitGatewayId ?? ''}`;
      }

      return entity?.Vpc?.id ?? null;
    }

    if (this.cloud === 'azure') {
      return convertToReadableId(getCustomProperties(entity)?.vnetId ?? '', 'azure');
    }

    return null;
  };

  getPathSegmentNetworkId = (pathSegment) => {
    const { entity, type } = pathSegment;

    if (this.cloud === 'aws') {
      if (type === VPC) {
        return entity?.id;
      }

      if (type === FIREWALL) {
        return pathSegment?.firewallSubnet?.VpcId;
      }

      if (type === CORE_NETWORK_SEGMENT) {
        return entity?.Name ?? entity?.CoreNetworkId ?? pathSegment?.coreNetworkId ?? null;
      }

      return entity?.VpcId ?? entity?.Vpc?.id ?? entity?.CoreNetworkId ?? entity?.TransitGatewayId ?? null;
    }

    if (this.cloud === 'azure') {
      return convertToReadableId(entity?.vnet?.id ?? getCustomProperties(entity)?.vnetId ?? '', 'azure');
    }

    return null;
  };

  getEntityIpAddress = (entity, isNetwork = false) => {
    if (this.cloud === 'aws') {
      if (isNetwork) {
        // the most likely entity is VPC or Subnet
        return entity?.Vpc?.CidrBlock ?? entity?.CidrBlock;
      }
      return entity?.PrivateIpAddress ?? entity?.CidrBlock;
    }

    if (this.cloud === 'azure') {
      if (isNetwork) {
        return entity?.vnet?.properties?.addressSpace?.addressPrefixes;
      }
      return entity?.properties?.addressPrefix;
    }

    return null;
  };

  getSegmentIpAddress = (pathSegment, isNetwork = false) => {
    const { entity } = pathSegment;

    return this.getEntityIpAddress(entity, isNetwork);
  };

  renderPathCard = (path, isReversePath) => {
    const { report } = this.state;
    const key = path.map((segment) => segment.value).join('-');

    /**
     * array of { region, regionPath: [] }[]
     * @type {*[]}
     */
    const regionMap = [];
    let segmentsInRegion = [];
    path.forEach((segment, index) => {
      const region = segment.region ?? GLOBAL_REGION;
      const prevRegion = path[index - 1]?.region ?? GLOBAL_REGION;

      const tgwId = segment.entity?.TransitGatewayId;
      const prevTgwId = path[index - 1]?.entity?.TransitGatewayId;

      const regionHasChanged = (region !== prevRegion || prevTgwId !== tgwId) && index > 1;
      if (regionHasChanged) {
        regionMap.push({ region: prevRegion, regionPath: segmentsInRegion });
        // new region, reset carry
        segmentsInRegion = [];
      }

      segmentsInRegion.push(segment);

      // always add when last index
      if (index === path.length - 1) {
        regionMap.push({ region, regionPath: segmentsInRegion });
      }
    });

    const pathHasErrors = path.some((segment) => segment.errors?.length);
    const isPathReachable = isReversePath ? report.get('results.returnReachable') : report.get('results.reachable');
    const renderedNetworks = [];

    return (
      <Card key={key} mb={2} p={2} style={{ width: 'fit-content' }}>
        <Flex justifyContent="space-between">
          <Flex alignItems="center" gap={1} fontSize="12px">
            <Icon
              icon={pathHasErrors || !isPathReachable ? FiXCircle : FiCheckCircle}
              color={pathHasErrors || !isPathReachable ? 'danger' : 'success'}
            />
            {path.map((segment, idx) => {
              const readableSegmentId = convertToReadableId(segment.value, this.cloud);
              const readableSegmentType = convertToReadableType(segment.type);
              const nextSegment = path[idx + 1];

              return (
                <Flex key={`${idx}-${segment.type}-${segment.value}`} alignItems="center">
                  <Flex flexDirection="column" px={1}>
                    <Flex gap={1} width="100%" justifyContent="start" alignItems="center">
                      <CloudIcon
                        entity={segment.type}
                        cloudProvider={this.cloud}
                        width={ICON_SIZE}
                        height={ICON_SIZE}
                      />
                      <Text as="b">{readableSegmentType}</Text>

                      {this.renderMatchingRoutePopover(nextSegment)}
                    </Flex>
                    <Text as="div" whiteSpace="nowrap" textOverflow="ellipsis">
                      {readableSegmentId}
                      {this.renderTextCopyButton(readableSegmentId)}
                    </Text>
                  </Flex>
                  {segment.errors?.length > 0 && (
                    <Tooltip
                      intent="danger"
                      content={
                        <Box>
                          {segment.errors?.map((error) => (
                            <Text key={`${readableSegmentId}-${error.message}`} as="div">
                              {error.message}
                            </Text>
                          ))}
                        </Box>
                      }
                    >
                      <Flex gap={1}>
                        {uniq(segment.errors, (error) => error.message).map((error) => {
                          const errorUrl = getConsoleUrl({
                            type: '',
                            value: error.nodes?.[0] ?? '',
                            nodeData: {
                              RegionName: segment.region ?? GLOBAL_REGION
                            }
                          });
                          return (
                            <LinkButton key={errorUrl} intent="danger" to={errorUrl} minimal blank ml="4px">
                              <Icon icon={BsFillExclamationTriangleFill} color="danger" />/{' '}
                            </LinkButton>
                          );
                        })}
                      </Flex>
                    </Tooltip>
                  )}
                  {idx < path.length - 1 && this.renderRightArrow()}
                </Flex>
              );
            })}
          </Flex>
        </Flex>

        {/* main box path rendering with vpc wrappers */}
        <Flex gap={1} width="100%" justifyContent="space-between">
          {regionMap.map((regionMapSegment, regionIndex) => {
            const { region, regionPath } = regionMapSegment;

            const regionPathToRender = regionPath.filter(
              (pathNode) => pathNode.type !== VPC && pathNode.type !== TRANSIT_GATEWAY
            );

            const networkMap = groupBy(
              regionPathToRender.filter((segment) => this.getPathSegmentNetworkId(segment)),
              (segment) => this.getPathSegmentNetworkId(segment)
            );

            let prevNetworkId = null;

            return (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={`${region}-${regionIndex}`}>
                <Box
                  bg="subnavBackground"
                  p={2}
                  my={2}
                  flex={regionIndex > 0 && regionIndex < regionMap.length - 1 ? 2 : 1}
                >
                  <Text mb={2} small as="div" fontWeight="bold">
                    {region ?? GLOBAL_REGION}
                  </Text>
                  <Flex justifyContent="space-evenly" gap={1}>
                    {regionPathToRender.map((segment, idx) => {
                      const readableSegmentId = convertToReadableId(segment.value, this.cloud);
                      const readableSegmentType = convertToReadableType(segment.type);

                      const networkId = this.getPathSegmentNetworkId(segment);

                      if (renderedNetworks.includes(networkId)) {
                        return null;
                      }
                      const networkType = this.getPathNetworkType(segment);
                      const networkName = this.getPathNetworkName(segment);
                      const accountId = segment?.entity ? getAwsAccountId(segment.entity) : null;
                      const hasNetworkWrapper = !!networkId;
                      const hasErrors = segment.errors?.length > 0;

                      const networkHasChanged = networkId !== prevNetworkId && prevNetworkId !== null;
                      prevNetworkId = networkId;

                      if (hasNetworkWrapper) {
                        const segmentsInNetwork = networkMap[networkId];
                        const NetworkBox = (
                          <React.Fragment>
                            {networkHasChanged && this.renderRightArrow()}
                            <Flex border="thinDotted" flexDirection="column" p={2} key={segment.value}>
                              <Box mb={1}>
                                <Flex justifyContent="center" gap={1}>
                                  <CloudIcon
                                    entity={this.cloud === 'aws' ? (networkType ?? VPC) : VNET}
                                    cloudProvider={this.cloud}
                                    width={24}
                                    height={24}
                                  />
                                  <Box>
                                    {networkType && (
                                      <Box flex={1} display="flex" gap={3} alignItems="center">
                                        <Text as="div" fontWeight="bold">
                                          {networkType}
                                        </Text>
                                        {accountId && (
                                          <Text fontsize="smaller" as="div" ml={2}>
                                            {accountId}
                                            {this.renderTextCopyButton(accountId)}
                                          </Text>
                                        )}
                                      </Box>
                                    )}
                                    <Text as="div" nowrap textOverflow="ellipsis">
                                      {networkName ? `${networkName} (${networkId})` : networkId}
                                      {this.renderTextCopyButton(networkId)}
                                    </Text>
                                    <Text as="div" muted small>
                                      {this.getSegmentIpAddress(segment, true)}
                                    </Text>
                                  </Box>
                                </Flex>
                              </Box>

                              <Flex flex={1} justifyContent="center" gap={1} alignItems="center">
                                {segmentsInNetwork.map((segmentInNetwork, index) => (
                                  <React.Fragment key={`${index}-${segmentInNetwork.type}-${segmentInNetwork.value}`}>
                                    {hasErrors && (
                                      <Tooltip
                                        intent="danger"
                                        content={
                                          <Box>
                                            {segmentInNetwork.errors?.map((error) => (
                                              <Text key={`${segmentInNetwork.value}-${error.message}`} as="div">
                                                {error.message}
                                              </Text>
                                            ))}
                                          </Box>
                                        }
                                      >
                                        {this.renderCloudItem(segmentInNetwork)}
                                      </Tooltip>
                                    )}
                                    {!hasErrors && this.renderCloudItem(segmentInNetwork)}
                                    {index !== segmentsInNetwork.length - 1 && this.renderRightArrow(16)}
                                  </React.Fragment>
                                ))}
                              </Flex>
                            </Flex>
                          </React.Fragment>
                        );

                        if (networkType !== TRANSIT_GATEWAY && networkType !== CORE_NETWORK_SEGMENT) {
                          renderedNetworks.push(networkId);
                        }

                        return NetworkBox;
                      }

                      // if entity does not exist in topology
                      if (!segment.entity) {
                        return (
                          <Flex gap={3} key={readableSegmentId}>
                            <Flex flexDirection="column" justifyContent="center" alignItems="center" gap="1">
                              <Text as="div" fontWeight="bold">
                                {readableSegmentType}
                              </Text>
                              {this.renderCloudItem(segment)}
                              <Callout intent="danger">Entity does not exist in topology.</Callout>
                            </Flex>
                          </Flex>
                        );
                      }

                      if (
                        idx !== regionPathToRender.length - 1 &&
                        this.getPathSegmentNetworkId(regionPathToRender[idx + 1].entity) === readableSegmentId
                      ) {
                        return null;
                      }

                      // transit gateway, NAT gateway, vpc peering and etc.. render
                      return (
                        <React.Fragment key={readableSegmentId}>
                          <Flex flexDirection="column" justifyContent="center" alignItems="center" gap="1">
                            <Text as="div" fontWeight="bold">
                              {readableSegmentType}
                            </Text>
                            {this.renderCloudItem(segment)}
                          </Flex>
                        </React.Fragment>
                      );
                    })}
                    {['InternetGateways', 'NatGateways'].includes(
                      regionPathToRender[regionPathToRender.length - 1]?.type
                    ) && (
                      <React.Fragment>
                        {this.renderRightArrow()}
                        <Flex flexDirection="column" justifyContent="center" alignItems="center" ml={2}>
                          <Icon icon={FiCloud} iconSize={64} />
                          <Text as="div" textAlign="center">
                            Internet
                          </Text>
                        </Flex>
                      </React.Fragment>
                    )}
                  </Flex>
                </Box>
                {regionIndex < regionMap.length - 1 && this.renderRightArrow()}
              </React.Fragment>
            );
          })}
        </Flex>
      </Card>
    );
  };

  typeToDetail(type) {
    if (type === 'Subnets') {
      return 'subnet_id';
    }

    if (type === 'vpc') {
      return 'vpc_id';
    }

    if (type === 'Instance') {
      return 'vm_id';
    }

    if (type === 'NetworkInterfaces') {
      return 'eni';
    }

    return type;
  }

  saveReport() {
    const { report } = this.state;
    const { history } = this.props;

    if (report.isNew) {
      this.setState({ loading: true });

      report.save().then(() => {
        history.replace(`/v4/cloud/pathfinder/${this.cloud}/${report.get('id')}`);
      });
    }
  }

  forceRebuildReport = () => {
    const { report } = this.state;

    if (!report) {
      return;
    }

    this.setState({ loading: true, error: undefined });
    this.fetchReportDetails(report, true).catch((error) => {
      this.setState({ error, loading: false });
    });
  };

  handleDrawerToggle = () => this.setState(({ drawerIsOpen }) => ({ drawerIsOpen: !drawerIsOpen }));

  get topologyLoader() {
    return (
      <Flex p={2} flexDirection="row" gap="2" alignItems="center" justifyContent="center" minWidth={475}>
        <Spinner size={24} />
        <Text as="div">Loading Cloud Topology...</Text>
      </Flex>
    );
  }

  render() {
    const { $auth, theme, reportObject, loadingText, sidebarDetailsPanel } = this.props;
    const { report, paths, returnPaths, loading, error, srcEntity, dstEntity, drawerIsOpen, loadingTopology } =
      this.state;

    let reportAiSummary = report?.get('results.summary');
    if (!reportAiSummary) {
      reportAiSummary = 'There was an error retrieving the Kentik AI Pathfinder Report Summary.';
    }

    const title = `Pathfinder Report: ${report?.get('name') ?? ''}`;
    const cloudProviderLabel = report?.get('cloud') === 'aws' ? 'AWS' : 'Azure';

    return (
      <Page
        drawerContents={
          loadingTopology ? this.topologyLoader : <HybridTopoSidebar sidebarDetailsPanel={sidebarDetailsPanel} />
        }
        drawerIsOpen={drawerIsOpen}
        drawerOnClose={(e) => {
          if ((e && e.key === 'Escape' && drawerIsOpen) || (e && e.type === 'click')) {
            this.setState({ drawerIsOpen: false });
          }
        }}
        drawerProps={{ showDockedInLargeFullScreen: false, showCloseButton: true }}
        title={title}
        subnavTools={
          <Button
            text="Details"
            icon={AiOutlineMenuFold}
            ml="2px"
            active={drawerIsOpen}
            onClick={this.handleDrawerToggle}
            disabled={!sidebarDetailsPanel}
            minimal
          />
        }
        parentLinks={[
          { link: '/v4/cloud/pathfinder', label: 'Cloud Pathfinder' },
          {
            link: `/v4/cloud/pathfinder/${report?.get('cloud') ?? 'aws'}`,
            label: `${report?.get('name') ? cloudProviderLabel : ''} Pathfinder Reports`
          }
        ]}
        gray
        pageProps={{
          overflow: 'auto'
        }}
      >
        <Suspense
          loading={loading}
          fallback={
            <Flex flexDirection="column" alignItems="center" justifyContent="center">
              <LoadingSkeleton height={300} />
              <Text muted as="div" ml={1}>
                {loadingText}
              </Text>
            </Flex>
          }
        >
          {report && (
            <>
              <Flex mb={2} justifyContent="space-between">
                <Flex gap={4}>
                  <LargeLabeledValue
                    label={`Source ${report.get('src_type')}`}
                    valueElement={
                      <CloudLinkWithIcon
                        cloud={this.cloud}
                        type={report.get('src_type')}
                        detail={this.typeToDetail(report.get('src_type'))}
                        value={convertToReadableId(report.get('src'), this.cloud)}
                        ipAddress={this.getEntityIpAddress(srcEntity)}
                      />
                    }
                  />
                  <LargeLabeledValue
                    label={`Destination ${report.get('dst_type')}`}
                    valueElement={
                      <CloudLinkWithIcon
                        cloud={this.cloud}
                        type={report.get('dst_type')}
                        detail={this.typeToDetail(report.get('dst_type'))}
                        value={convertToReadableId(report.get('dst'), this.cloud)}
                        ipAddress={this.getEntityIpAddress(dstEntity)}
                      />
                    }
                  />
                  <LargeLabeledValue label="Port" valueElement={report.get('dst_port')} />
                  <LargeLabeledValue label="Protocol" valueElement={report.get('protocol')} />
                  <LargeLabeledValue
                    label="Last Run"
                    valueElement={moment(report.get('edate')).format(DEFAULT_DATETIME_FORMAT)}
                  />
                </Flex>
                <Box>
                  <Flex gap={1}>
                    <Button icon="refresh" onClick={this.forceRebuildReport} disabled={loading}>
                      Regenerate Report
                    </Button>
                    {report.isNew && !reportObject && (
                      <Button icon="floppy-disk" intent="warning" onClick={() => this.saveReport()}>
                        Save Report
                      </Button>
                    )}
                  </Flex>
                </Box>
              </Flex>
            </>
          )}
          {error && (
            <Box bg={theme.colors.dangerBackground} textAlign="center">
              <Text as="h3">Error: {error}</Text>
            </Box>
          )}

          {!error && report && (
            <>
              <Flex gap={4}>
                {$auth.canUseAiTools && (
                  <Dismissible name={`con-checker-${report.get('id')}`} noPersist>
                    {({ onDismiss }) => (
                      <CalloutKentikAi minWidth={350} width={350} className="bp4-running-text">
                        <SummaryWrapper>
                          <Markdown>{reportAiSummary}</Markdown>
                        </SummaryWrapper>
                        <Button small onClick={onDismiss} text="Close Summary" />
                      </CalloutKentikAi>
                    )}
                  </Dismissible>
                )}

                <Flex flexDirection="column" width="100%">
                  <Heading level={5}>Forward Paths</Heading>
                  <PathScrollContainer>{paths.map((path) => this.renderPathCard(path))}</PathScrollContainer>

                  <Heading level={5}>Return Paths</Heading>
                  <PathScrollContainer>
                    {returnPaths.map((path) => this.renderPathCard(path, true))}
                  </PathScrollContainer>
                </Flex>
              </Flex>
            </>
          )}
        </Suspense>
      </Page>
    );
  }
}
